/**
 * Test Flow Coordination - 2025 Best Practices
 * Orchestrates onboarding and wizard stores for seamless user experience
 *
 * Purpose: Provide a single source of truth for test flow state coordination
 * - Detects resume scenarios
 * - Coordinates resets between stores
 * - Manages state conflicts
 * - Provides smart navigation logic
 */

import { computed } from 'vue'
import { useOnboardingStore } from '../onboarding'
import { useWizardStore } from '../wizard'
import { TOTAL_QUESTIONS } from '@/data/learningTest'

/**
 * Test flow states representing different user scenarios
 */
export type TestFlowState =
  | 'fresh-start'           // No data, start from beginning
  | 'partial-onboarding'    // Onboarding in progress
  | 'onboarding-complete'   // Onboarding done, wizard not started
  | 'test-in-progress'      // Wizard in progress with onboarding complete
  | 'test-complete'         // Test finished, results available
  | 'orphaned-test'         // Wizard progress without onboarding (edge case)

/**
 * Resume options for different scenarios
 */
export interface ResumeOptions {
  canResumeOnboarding: boolean
  canResumeTest: boolean
  canStartFresh: boolean
  canEditInfo: boolean
  onboardingProgress: number
  testProgress: number
  answeredQuestions: number
  totalQuestions: number
}

/**
 * Coordination hooks for test flow orchestration
 *
 * @example
 * ```ts
 * const { flowState, resumeOptions, resetEntireFlow } = useTestFlowCoordination()
 *
 * if (flowState.value === 'test-in-progress') {
 *   // Show resume test banner
 * }
 * ```
 */
export function useTestFlowCoordination() {
  const onboarding = useOnboardingStore()
  const wizard = useWizardStore()

  // ===========================
  // STATE DETECTION
  // ===========================

  /**
   * Check if onboarding has any data
   */
  const hasOnboardingData = computed<boolean>(() => {
    return !!(
      onboarding.formData.childName ||
      onboarding.formData.childAge ||
      onboarding.currentStep > 1
    )
  })

  /**
   * Check if onboarding is complete
   */
  const hasOnboardingComplete = computed<boolean>(() => {
    return onboarding.isCompleted
  })

  /**
   * Check if wizard has progress (any answers)
   */
  const hasWizardProgress = computed<boolean>(() => {
    return Object.keys(wizard.answers).length > 0
  })

  /**
   * Check if wizard is complete
   */
  const hasWizardComplete = computed<boolean>(() => {
    return wizard.phase2Complete && !!wizard.analysisResult
  })

  /**
   * Calculate onboarding progress percentage
   */
  const onboardingProgressPercent = computed<number>(() => {
    const totalSteps = 5
    return Math.round((onboarding.currentStep / totalSteps) * 100)
  })

  /**
   * Calculate test progress percentage
   */
  const testProgressPercent = computed<number>(() => {
    const answeredCount = Object.keys(wizard.answers).length
    return Math.round((answeredCount / TOTAL_QUESTIONS) * 100)
  })

  /**
   * Get number of answered questions
   */
  const answeredQuestionsCount = computed<number>(() => {
    return Object.keys(wizard.answers).length
  })

  // ===========================
  // FLOW STATE DETERMINATION
  // ===========================

  /**
   * Determine current test flow state
   * This is the main state machine for the test flow
   */
  const flowState = computed<TestFlowState>(() => {
    // Test complete scenario
    if (hasWizardComplete.value) {
      return 'test-complete'
    }

    // Test in progress with onboarding complete
    if (hasWizardProgress.value && hasOnboardingComplete.value) {
      return 'test-in-progress'
    }

    // Orphaned test (wizard progress without onboarding - edge case)
    if (hasWizardProgress.value && !hasOnboardingComplete.value) {
      return 'orphaned-test'
    }

    // Onboarding complete, wizard not started
    if (hasOnboardingComplete.value && !hasWizardProgress.value) {
      return 'onboarding-complete'
    }

    // Partial onboarding in progress
    if (hasOnboardingData.value && !hasOnboardingComplete.value) {
      return 'partial-onboarding'
    }

    // Fresh start - no data
    return 'fresh-start'
  })

  /**
   * Get resume options based on current flow state
   */
  const resumeOptions = computed<ResumeOptions>(() => {
    const state = flowState.value

    return {
      canResumeOnboarding: state === 'partial-onboarding',
      canResumeTest: state === 'test-in-progress',
      canStartFresh: true, // Always allow fresh start
      canEditInfo: hasOnboardingComplete.value || hasWizardProgress.value,
      onboardingProgress: onboardingProgressPercent.value,
      testProgress: testProgressPercent.value,
      answeredQuestions: answeredQuestionsCount.value,
      totalQuestions: TOTAL_QUESTIONS
    }
  })

  /**
   * Check if user should see resume banner on home page
   */
  const shouldShowResumeBanner = computed<boolean>(() => {
    console.log(flowState.value);
    return flowState.value === 'test-in-progress' ||
      flowState.value === 'partial-onboarding' ||
      flowState.value === 'onboarding-complete'; //FIXME: make it proerply solid typed and handle all caeses properly best ux

  })

  /**
   * Check if user is at a point where they can edit basic info
   */
  const canEditBasicInfo = computed<boolean>(() => {
    return hasOnboardingComplete.value || hasWizardProgress.value
  })

  // ===========================
  // ACTIONS
  // ===========================

  /**
   * Reset entire test flow (onboarding + wizard)
   * Use this when user wants to start completely fresh
   */
  function resetEntireFlow(): void {
    console.log('🔄 Resetting entire test flow...')

    // Reset both stores
    onboarding.reset()
    wizard.resetStore()

    console.log('✅ Entire test flow reset successfully')
  }

  /**
   * Transfer onboarding data to wizard
   * Used when onboarding completes
   * ✅ UPDATED: Now transfers all fields properly without string concatenation
   */
  function transferOnboardingToWizard(): void {
    if (!hasOnboardingComplete.value) {
      console.warn('⚠️ Onboarding not complete, skipping transfer')
      return
    }

    const data = onboarding.formData

    // ✅ Transfer all basic info fields (no concatenation!)
    wizard.setBasicInfo({
      childName: data.childName,
      childAge: Number(data.childAge) || 0,
      childSchool: data.childSchool,
      childSituation: data.childSituation,
      childInterests: data.childInterests,
      childGender: data.childGender,
    })

    // Transfer onboarding context separately
    wizard.setOnboardingContext({
      challenges: data.challenges,
      parentFeelings: data.parentFeelings,
      selectedGoals: data.selectedGoals,
      startedAt: data.startedAt,
      completedAt: data.completedAt,
    })

    console.log('✅ Onboarding data transferred to wizard (all fields)')
  }

  /**
   * Sync wizard basic info back to onboarding
   * Used when user edits info during wizard
   * ✅ UPDATED: Now syncs all fields including childInterests and childGender
   */
  function syncWizardToOnboarding(): void {
    if (!wizard.basicInfo.childName) {
      console.warn('⚠️ No wizard data to sync')
      return
    }

    onboarding.updateField('childName', wizard.basicInfo.childName)
    onboarding.updateField('childAge', wizard.basicInfo.childAge)
    onboarding.updateField('childSchool', wizard.basicInfo.childSchool)
    onboarding.updateField('childSituation', wizard.basicInfo.childSituation)
    onboarding.updateField('childInterests', wizard.basicInfo.childInterests) // ← NEW
    onboarding.updateField('childGender', wizard.basicInfo.childGender) // ← NEW

    console.log('✅ Wizard data synced back to onboarding (all fields)')
  }

  /**
   * Handle orphaned test scenario
   * When wizard has progress but onboarding is missing
   */
  function resolveOrphanedTest(): 'complete-onboarding' | 'discard-test' {
    console.warn('⚠️ Orphaned test detected (wizard without onboarding)')

    // If wizard has basicInfo, we can recover
    if (wizard.basicInfo.childName) {
      console.log('🔄 Attempting to recover onboarding from wizard data')
      syncWizardToOnboarding()
      onboarding.isCompleted = true
      return 'complete-onboarding'
    }

    // Otherwise, suggest discarding the test
    console.warn('❌ Cannot recover - suggest discarding test')
    return 'discard-test'
  }

  /**
   * Prepare for test start
   * Ensures onboarding is complete and data is transferred
   */
  function prepareTestStart(): boolean {
    if (!hasOnboardingComplete.value) {
      console.error('❌ Cannot start test - onboarding not complete')
      return false
    }

    // Transfer onboarding data if wizard doesn't have it
    if (!wizard.basicInfo.childName) {
      transferOnboardingToWizard()
    }

    // Start test session in backend
    wizard.startTestSession().catch((error: unknown) => {
      console.warn('⚠️ Failed to start test session (non-critical):', error)
    })

    // Start timing
    wizard.startTestTiming()

    console.log('✅ Test start prepared')
    return true
  }

  /**
   * Validate that stores are in sync
   * Useful for debugging and ensuring data consistency
   */
  function validateStoreSync(): {
    isValid: boolean
    issues: string[]
  } {
    const issues: string[] = []

    // Check if wizard has progress but onboarding is incomplete
    if (hasWizardProgress.value && !hasOnboardingComplete.value) {
      issues.push('Wizard has progress but onboarding is incomplete')
    }

    // Check if basic info matches
    if (hasOnboardingComplete.value && wizard.basicInfo.childName) {
      if (onboarding.formData.childName !== wizard.basicInfo.childName) {
        issues.push('Child name mismatch between stores')
      }
      if (onboarding.formData.childAge !== wizard.basicInfo.childAge) {
        issues.push('Child age mismatch between stores')
      }
    }

    return {
      isValid: issues.length === 0,
      issues
    }
  }

  // ===========================
  // RETURN PUBLIC API
  // ===========================

  return {
    // State detection
    hasOnboardingData,
    hasOnboardingComplete,
    hasWizardProgress,
    hasWizardComplete,
    onboardingProgressPercent,
    testProgressPercent,
    answeredQuestionsCount,

    // Flow state
    flowState,
    resumeOptions,
    shouldShowResumeBanner,
    canEditBasicInfo,

    // Actions
    resetEntireFlow,
    transferOnboardingToWizard,
    syncWizardToOnboarding,
    resolveOrphanedTest,
    prepareTestStart,
    validateStoreSync
  }
}

/**
 * Type exports for consumers
 */
export type TestFlowCoordination = ReturnType<typeof useTestFlowCoordination>
